import { useEffect, useState } from "react";

export interface VisualViewportState {
  /** Current visual viewport height in px */
  height: number;
  /**
   * Pixels to shift a position:fixed element upward so it sits above the keyboard.
   * = window.innerHeight - visualViewport.height - visualViewport.offsetTop
   * Zero when no keyboard is open.
   */
  keyboardOffset: number;
}

/**
 * useVisualViewportHeight
 *
 * Tracks `window.visualViewport` to handle the iOS soft keyboard.
 *
 * On iOS Safari, `position: fixed` elements are anchored to the layout viewport
 * (full page size) and don't move when the keyboard opens. This hook returns:
 *
 * - `height`: the visible viewport height (shrinks when keyboard opens)
 * - `keyboardOffset`: how many px to translate a fixed element upward to clear the keyboard
 *
 * Returns `null` on SSR or in browsers that don't support `visualViewport`.
 */
export function useVisualViewportHeight(enabled = true): VisualViewportState | null {
  const [state, setState] = useState<VisualViewportState | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Reset stale state so the next open doesn't briefly flash the old viewport height.
      setState(null);
      return;
    }

    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;

    const update = () => {
      // keyboard offset = how far the visual viewport has been pushed up from the bottom
      // of the layout viewport by the keyboard.
      const keyboardOffset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop ?? 0));
      setState({ height: vv.height, keyboardOffset });
    };

    update();

    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return state;
}
